import classNames from "classnames";
import { get } from "lodash";
import { useTranslation } from "react-i18next";
import { BiCheck } from "react-icons/bi";
import {
  MdAdd,
  MdOutlineAddCircleOutline,
  MdOutlineRefresh,
  MdOutlineSecurity,
} from "react-icons/md";
import Button from "shared/components/Button";
import { NAME_SPACES } from "shared/locales/constants";
import { COLORS } from "shared/style/colors";
import { IMPORT_STRATEGIES } from "./constants";
import StepCard from "./StepCard";

const STRATEGIES = [
  {
    id: IMPORT_STRATEGIES.NEW_ONLY,
    icon: MdAdd,
    color: COLORS.C_SUCCESS,
  },
  {
    id: IMPORT_STRATEGIES.UPDATE_ONLY,
    icon: MdOutlineRefresh,
    color: COLORS.C_PRIMARY,
  },
  {
    id: IMPORT_STRATEGIES.ALL,
    icon: MdOutlineSecurity,
    color: COLORS.C_PRIMARY,
  },
];

const StepAnalysis = ({
  preview,
  strategy,
  onStrategyChange,
  onNext,
  confirmed,
  onConfirmedChange,
  onBack,
  backLabel,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.INSURANCE_PLAN);
  const IMPORT = t("IMPORT", { returnObjects: true });

  const plans = get(preview, "plans", []);
  const totalPlans = plans.length;
  const newPlans = get(preview, "newPlans", 0);
  const existingPlans = get(preview, "existingPlans", 0);
  const needsConfirmation = strategy === IMPORT_STRATEGIES.ALL;

  const launchButton = (
    <Button
      type="primary"
      onClick={onNext}
      disabled={needsConfirmation && !confirmed}
    >
      <BiCheck size={20} />
      {IMPORT.ANALYSIS.LAUNCH_IMPORT}
    </Button>
  );

  return (
    <StepCard
      title={IMPORT.ANALYSIS.TITLE}
      description={IMPORT.ANALYSIS.DESCRIPTION}
      onBack={onBack}
      backLabel={backLabel}
      actions={launchButton}
    >
      <div className="grid--3 gap-16 mb-24">
        <div className="pricers-import__analysis-stat pricers-import__analysis-stat--total">
          <div className="pricers-import__analysis-value pricers-import__analysis-value--total">
            {totalPlans}
          </div>
          <div className="pricers-import__analysis-label pricers-import__analysis-label--muted">
            {IMPORT.ANALYSIS.TOTAL_PLANS}
          </div>
        </div>
        <div className="pricers-import__analysis-stat pricers-import__analysis-stat--new">
          <div className="d-flex align--center justify--center gap-6">
            <MdOutlineAddCircleOutline size={22} color={COLORS.C_SUCCESS} />
            <span className="pricers-import__analysis-value pricers-import__analysis-value--new">
              {newPlans}
            </span>
          </div>
          <div className="pricers-import__analysis-label pricers-import__analysis-label--new">
            {IMPORT.ANALYSIS.NEW_PLANS}
          </div>
          <div className="pricers-import__analysis-sublabel">
            {IMPORT.ANALYSIS.NEW_PLANS_DESC}
          </div>
        </div>
        <div className="pricers-import__analysis-stat pricers-import__analysis-stat--existing">
          <div className="d-flex align--center justify--center gap-6">
            <MdOutlineRefresh size={22} color={COLORS.C_PRIMARY} />
            <span className="pricers-import__analysis-value pricers-import__analysis-value--existing">
              {existingPlans}
            </span>
          </div>
          <div className="pricers-import__analysis-label pricers-import__analysis-label--existing">
            {IMPORT.ANALYSIS.EXISTING_PLANS}
          </div>
          <div className="pricers-import__analysis-sublabel">
            {IMPORT.ANALYSIS.EXISTING_PLANS_DESC}
          </div>
        </div>
      </div>

      <h4 className="m-bottom-12">{IMPORT.ANALYSIS.STRATEGY_TITLE}</h4>
      <div className="pricers-import__strategy-list">
        {STRATEGIES.map((strat) => {
          const Icon = strat.icon;
          const isSelected = strategy === strat.id;
          return (
            <div
              key={strat.id}
              onClick={() => {
                onStrategyChange(strat.id);
                onConfirmedChange(false);
              }}
              className={classNames("pricers-import__strategy-option", {
                "pricers-import__strategy-option--selected": isSelected,
              })}
            >
              <div
                className={classNames("pricers-import__strategy-radio", {
                  "pricers-import__strategy-radio--selected": isSelected,
                })}
              />
              <div className="pricers-import__strategy-icon">
                <Icon
                  size={20}
                  color={isSelected ? strat.color : COLORS.C_GRAY}
                />
              </div>
              <div className="pricers-import__strategy-content">
                <div className="pricers-import__strategy-name">
                  {IMPORT.ANALYSIS.STRATEGIES[strat.id].LABEL}
                </div>
                <div className="pricers-import__strategy-desc">
                  {IMPORT.ANALYSIS.STRATEGIES[strat.id].DESCRIPTION}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {needsConfirmation && (
        <div className="pricers-import__confirm-banner pricers-import__confirm-banner--warning">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => onConfirmedChange(e.target.checked)}
            className="pricers-checkbox pricers-checkbox--lg"
          />
          <span className="pricers-import__confirm-label">
            {IMPORT.ANALYSIS.CONFIRM_ALL}
          </span>
        </div>
      )}
    </StepCard>
  );
};

export default StepAnalysis;
